/**
 * YOUR HAND'S FAN — drawn the same way by حکم and بی‌دل, and by پاسور and شلم next: every card at
 * the fan's own scale (a full hand overlaps more, it never shrinks a card), neighbours at most 28 px
 * apart, the whole fan no wider than 340 px — a full 13-card fan inside a phone width — and each card
 * on one arc: the centre cards ride highest, the edges fan down a touch and tilt out, the whole arc
 * raised `baseLift` off the bottom of the fan's box.
 *
 * What a table decides for itself is a card's RISE over the arc (a legal card, a lifted one, بی‌دل's
 * pick and received cards) and the rest of its wrapper (z-index, transition, a glow).
 * `lift-geometry.test.ts` reads the arc, the tilt and `baseLift` out of THIS source to prove a lifted
 * card clears the strip row above the fan.
 */

/** Your hand's cards are the `large` 50×70 box drawn at this scale (57.5×80.5) — at every width: a
 *  full hand overlaps more (`step`), it never shrinks a card. On حکم it is also the CEILING of the
 *  played cards' scale (`trickCardScale` in `HokmTable`): a card you play lands on the felt at most
 *  the size it had in your hand. */
export const HAND_CARD_SCALE = 1.15;

/** The fan's box height (px): the band your hand is drawn in, its cards bottom-aligned in it. */
export const HAND_FAN_BOX_H = 126;

/** px the whole fan is raised off the bottom edge */
const baseLift = 28;

/** The step between neighbouring cards and the fan's width, for `n` cards at `cardScale`. The
 *  footprint is the `large` card's 50 px box scaled — 57 px at the tables' 1.15: 50 × 1.15 is
 *  57.49999999999999 in floating point, so the «~58 px» it was always described as rounds to 57.
 *  `budget` is the width the fan may take — 340 px, the cap every fan was drawn to, unless a table
 *  hands it less ({@link handFanBudget}: a narrow screen). */
export function handFanLayout(
  n: number,
  cardScale: number,
  budget = 340,
): { step: number; width: number } {
  const ew = Math.round(50 * cardScale); // effective fanned-card footprint
  const step = n > 1 ? Math.min(28, (budget - ew) / (n - 1)) : 0;
  return { step, width: (n - 1) * step + ew };
}

/**
 * THE FAN ON A NARROW TABLE (شلم P1 Task 3, W6: «the hand runs off both edges»). 340 px is wider
 * than a 320 px screen leaves a fan — the hand's row is the table less 2 × 20 px of padding, and an
 * edge card's tilted top corner reaches past its own box besides — so a table that measures its own
 * width asks here for the widest fan whose cards all stay `margin` px inside both edges.
 *
 * The fan box is centred in the table; its cards span `(n − 1) × step + 50` of it (its `width` adds
 * `ew − 50`, so they sit `(ew − 50) / 2` left of its centre); and the edge card — its tilt is at most
 * `offCap` steps' worth ({@link handCardPlacement}) — reaches `reach` = `25s·cos θ + 70s·sin θ` past
 * its pivot, the box's bottom centre, at the card's scale `s`. So the fan stays inside when
 * `(n − 1) × step ≤ tableW − (ew − 50) − 2·margin − 2·reach`, i.e. a budget (`(n − 1) × step + ew`)
 * of `tableW + 50 − 2·margin − 2·reach` — never more than the 340 every fan is capped at.
 */
export function handFanBudget(
  tableW: number,
  cardScale: number,
  offCap: number,
  margin = 3,
): number {
  const theta = (offCap * 2.6 * Math.PI) / 180; // the edge card's tilt (handCardPlacement's 2.6°)
  const reach = 25 * cardScale * Math.cos(theta) + 70 * cardScale * Math.sin(theta);
  return Math.min(340, tableW + 50 - 2 * margin - 2 * reach);
}

/** Where card `i` of `n` sits in the fan (`step` from {@link handFanLayout}), `rise` px over the arc,
 *  drawn at `cardScale` — the first three properties of its wrapper's style, which pivots on its
 *  bottom centre. `offCap`: a fan whose edge cards sit more than `offCap` steps from its centre is
 *  ARCED and TILTED as one whose edges sit at `offCap` (its cards' offsets scaled down together) —
 *  شلم's 16-card کف would otherwise drop its edge cards ~40 px below the fan's box; a fan inside
 *  the cap (every حکم / بی‌دل hand: 13 cards, 6 steps out) is drawn exactly as it always was. */
export function handCardPlacement(
  i: number,
  n: number,
  step: number,
  rise: number,
  cardScale: number,
  offCap = Infinity,
): { left: number; transformOrigin: string; transform: string } {
  const off = (i - (n - 1) / 2) * Math.min(1, offCap / ((n - 1) / 2));
  // Center cards ride highest; edges fan down a touch — the whole arc is
  // raised by `baseLift`, and a legal card lifts further.
  const lift = Math.abs(off) * Math.abs(off) * 1.2 - rise - baseLift;
  return {
    left: i * step,
    transformOrigin: "bottom center",
    transform: `translateY(${lift}px) rotate(${off * 2.6}deg) scale(${cardScale})`,
  };
}
